// rag-service KB HTTP client (control plane).
//
// All routes are reached through the Hodor regional gateway (`baseService` is anchored at
// REACT_APP_REGION_AUTH) under the `/v1/rag-service` prefix. Hodor strips `rag-service` and injects
// the trusted X-Org-Uuid header so rag-service authorizes the call against the org (so e.g.
// `/v1/rag-service/kb/crawl` reaches rag's `/v1/kb/crawl`).
//
// These functions return rag-service's BARE response bodies (no `{ data }` envelope). The mapping
// into the console-facing `RKnowledgeBase` shapes — and the re-wrapping into the envelope the
// React-Query hooks expect — lives in rag-adapter.ts / flow-service.ts, not here.

import type {
  RagCrawlCancelResponse,
  RagCrawlJobResponse,
  RagCrawlStatusResponse,
  RagDeletedResponse,
  RagDiscoverRequest,
  RagDiscoverResponse,
  RagFetchCollectionArticlesResponse,
  RagFetchIdResponse,
  RagKbStatusResponse,
  RagKnowledgeBaseCreateRequest,
  RagKnowledgeBaseResponse,
  RagMcpArticle,
  RagMcpFetchPollResponse,
  RagMcpSource,
  RagPatchDocResponse,
  RagResyncResponse,
  RagRetryResponse,
  RagSourceSpec,
  RagUpdateDocumentRequest,
  RagUpdateKnowledgeBaseRequest,
  RagUrlCrawlRequest,
} from 'v2/pages/flow/knowledge-base/rag-types'
import { baseService } from './base-services'

const RAG_BASE = '/v1/rag-service'

export const RAG_KB_ENDPOINTS = {
  kbs: `${RAG_BASE}/kb`,
  kb: (id: string) => `${RAG_BASE}/kb/${id}`,
  kbStatus: (id: string) => `${RAG_BASE}/kb/${id}/status`,
  kbSources: (id: string) => `${RAG_BASE}/kb/${id}/sources`,
  kbSource: (id: string, sourceUuid: string) => `${RAG_BASE}/kb/${id}/sources/${sourceUuid}`,
  kbSourceResync: (id: string, sourceUuid: string) =>
    `${RAG_BASE}/kb/${id}/sources/${sourceUuid}/resync`,
  kbUpload: (id: string) => `${RAG_BASE}/kb/${id}/upload`,
  kbResync: (id: string) => `${RAG_BASE}/kb/${id}/resync`,
  kbDocument: (id: string, docUuid: string) => `${RAG_BASE}/kb/${id}/documents/${docUuid}`,
  kbDocumentRetry: (id: string, docUuid: string) =>
    `${RAG_BASE}/kb/${id}/documents/${docUuid}/retry`,
  discover: `${RAG_BASE}/kb/discover`,
  // Crawl Preview (literal paths — mounted before the /kb/{id} catch-all in rag).
  crawl: `${RAG_BASE}/kb/crawl`,
  crawlStatus: (jobId: string) => `${RAG_BASE}/kb/crawl/${jobId}`,
  crawlCancel: (jobId: string) => `${RAG_BASE}/kb/crawl/${jobId}/cancel`,
  // Connected-app (MCP) fetch. `articles=false` builds a SPACES-ONLY tree (lazy picker): the worker
  // returns collections (Confluence spaces) with NO pages, so a 11k-page workspace renders in seconds;
  // each space's pages are fetched on expand via fetchCollectionArticles.
  fetchDocs: (source: RagMcpSource, sourceId?: string, articles = true) =>
    `${RAG_BASE}/kb/fetch-documents-from-source?source=${source}${sourceId ? `&source_id=${sourceId}` : ''}&articles=${articles}`,
  fetchDocsByFetchId: (fetchId: string) => `${RAG_BASE}/kb/fetch-documents-from-source/${fetchId}`,
  fetchCollectionArticles: (source: RagMcpSource, collectionId: string, sourceId?: string) =>
    `${RAG_BASE}/kb/fetch-collection-articles?source=${source}&collection_id=${encodeURIComponent(collectionId)}${sourceId ? `&source_id=${sourceId}` : ''}`,
  fetchDoc: (source: RagMcpSource, id: string, sourceId?: string) =>
    `${RAG_BASE}/kb/fetch-document-from-source?source=${source}&id=${id}${sourceId ? `&source_id=${sourceId}` : ''}`,
} as const

// ── KB CRUD ─────────────────────────────────────────────────────────────────────────────────────

export const ragListKbs = () => baseService.get<RagKnowledgeBaseResponse[]>(RAG_KB_ENDPOINTS.kbs)

export const ragGetKb = (id: string) =>
  baseService.get<RagKnowledgeBaseResponse>(RAG_KB_ENDPOINTS.kb(id), undefined, {
    validateStatus: (status) => status === 200 || status === 404,
  })

export const ragGetKbStatus = (id: string) =>
  baseService.get<RagKbStatusResponse>(RAG_KB_ENDPOINTS.kbStatus(id), undefined, {
    validateStatus: (status) => status === 200 || status === 404,
  })

export const ragCreateKb = (body: RagKnowledgeBaseCreateRequest) =>
  baseService.post<RagKnowledgeBaseResponse>(RAG_KB_ENDPOINTS.kbs, body)

export const ragUpdateKb = (id: string, body: RagUpdateKnowledgeBaseRequest) =>
  baseService.patch<RagKnowledgeBaseResponse>(RAG_KB_ENDPOINTS.kb(id), body)

export const ragDeleteKb = (id: string) =>
  baseService.delete<RagDeletedResponse>(RAG_KB_ENDPOINTS.kb(id))

export const ragResyncKb = (id: string) =>
  baseService.post<RagResyncResponse>(RAG_KB_ENDPOINTS.kbResync(id))

// ── sources ─────────────────────────────────────────────────────────────────────────────────────

export const ragAddSources = (id: string, sources: RagSourceSpec[]) =>
  baseService.post<RagKnowledgeBaseResponse>(RAG_KB_ENDPOINTS.kbSources(id), sources)

export const ragDeleteSource = (id: string, sourceUuid: string) =>
  baseService.delete<RagDeletedResponse>(RAG_KB_ENDPOINTS.kbSource(id, sourceUuid))

export const ragResyncSource = (id: string, sourceUuid: string) =>
  baseService.post<RagResyncResponse>(RAG_KB_ENDPOINTS.kbSourceResync(id, sourceUuid))

// ── documents ───────────────────────────────────────────────────────────────────────────────────

export const ragUpdateDoc = (id: string, docUuid: string, body: RagUpdateDocumentRequest) =>
  baseService.patch<RagPatchDocResponse>(RAG_KB_ENDPOINTS.kbDocument(id, docUuid), body)

export const ragDeleteDoc = (id: string, docUuid: string) =>
  baseService.delete<RagDeletedResponse>(RAG_KB_ENDPOINTS.kbDocument(id, docUuid))

export const ragRetryDoc = (id: string, docUuid: string) =>
  baseService.post<RagRetryResponse>(RAG_KB_ENDPOINTS.kbDocumentRetry(id, docUuid))

// ── crawl preview (fire / poll / STOP) ──────────────────────────────────────────────────────────

export const ragStartCrawl = (body: RagUrlCrawlRequest) =>
  baseService.post<RagCrawlJobResponse>(RAG_KB_ENDPOINTS.crawl, body)

export const ragGetCrawlStatus = (jobId: string) =>
  baseService.get<RagCrawlStatusResponse>(RAG_KB_ENDPOINTS.crawlStatus(jobId))

export const ragCancelCrawl = (jobId: string) =>
  baseService.post<RagCrawlCancelResponse>(RAG_KB_ENDPOINTS.crawlCancel(jobId))

export const ragDiscover = (body: RagDiscoverRequest) =>
  baseService.post<RagDiscoverResponse>(RAG_KB_ENDPOINTS.discover, body)

// ── connected-app (MCP) fetch ────────────────────────────────────────────────────────────────────

export const ragFetchDocsFromSource = (source: RagMcpSource, sourceId?: string, articles = true) =>
  baseService.get<RagFetchIdResponse>(RAG_KB_ENDPOINTS.fetchDocs(source, sourceId, articles))

export const ragFetchDocsByFetchId = (fetchId: string) =>
  baseService.get<RagMcpFetchPollResponse>(RAG_KB_ENDPOINTS.fetchDocsByFetchId(fetchId))

export const ragFetchCollectionArticles = (
  source: RagMcpSource,
  collectionId: string,
  sourceId?: string,
) =>
  baseService.get<RagFetchCollectionArticlesResponse>(
    RAG_KB_ENDPOINTS.fetchCollectionArticles(source, collectionId, sourceId),
  )

export const ragFetchDocFromSource = (source: RagMcpSource, id: string, sourceId?: string) =>
  baseService.get<RagMcpArticle | null>(RAG_KB_ENDPOINTS.fetchDoc(source, id, sourceId))
